import { createApi } from '@reduxjs/toolkit/query/react'
import { customBaseQuery } from '../../shared/helpers'

const parseLabelData = (course) => {
  if (!course.labelData) return course

  return {
    ...course,
    labelData: typeof course.labelData === 'string'
      ? JSON.parse(course.labelData)
      : course.labelData,
  }
}

export const coursesApi = createApi({
  reducerPath: 'coursesApi',
  baseQuery: customBaseQuery, 
  endpoints: (build) => ({
    getCourses: build.query({
      query: (args) => ({
        urlKey: 'fetchDataUrl',
        params: args 
      }),
    
      serializeQueryArgs: ({ queryArgs }) => {
        return queryArgs.filters; 
      },
    
      transformResponse: (response) => {
        return {
          ...response,
          courses: response.courses.map(parseLabelData),
        }
      },

      merge: (currentCache, newResponse, { arg }) => {
        if (arg.page === 1) {
          return newResponse;
        }
      
        return {
          ...newResponse,
          courses: [
            ...(currentCache?.courses || []),
            ...newResponse.courses
          ]
        };
      },
    }),
    getComplianceStats: build.query({
      query: () => ({
        urlKey: 'fetchComplianceStatsUrl',
        params: {} 
      }),
      transformResponse: (response, meta, arg) => {
        return response.stats; 
      },
    }),
    getPossibleLocations: build.query({
      query: (args) => ({
        urlKey: 'fetchPossibleLocationsUrl',
        params: args
      }),
      transformResponse: (response) => {
        return response.location_list;
      },
    }),
    getCrsCourses: build.query({
      query: (args) => ({
        urlKey: 'fetchCrsCoursesUrl',
        params: args,
      }),
      transformResponse: (response) => {
        if(!response?.items?.length) {
          return []
        }

        return response.items.map((course) => {
          return { value: course.id, label: course.item}
        })
      }
    }),
    getScorecentreCourses: build.query({
      query: (args) => ({
        urlKey: 'fetchScorecentreCoursesUrl',
        params: args,
      }),
      transformResponse: (response) => {
        if (!response?.items?.length) return []
        return response.items.map((course) => ({ value: course.id, label: course.item }))
      },
    }),
    getGolfAdvisorCourses: build.query({
      query: (args) => ({
        urlKey: 'fetchGolfAdvisorCoursesUrl',
        params: args,
      }),
      transformResponse: (response) => {
        if (!response?.items?.length) return []
        return response.items.map((course) => ({ value: course.id, label: course.item }))
      },
    }),
    getWhsUkCourses: build.query({
      query: (args) => ({
        urlKey: 'fetchWhsUkCoursesUrl',
        params: args,
      }),
      transformResponse: (response) => {
        if (!response?.items?.length) return []
        return response.items.map((course) => ({ value: course.id, label: course.item }))
      },
    }),
    getNgfCoursesForClub: build.query({
      query: (args) => ({
        urlKey: 'fetchNgfCoursesUrl',
        params: args,
      }),
      transformResponse: (response) => {
        if (!response?.length) return []
        return response.map((course) => ({ value: course.id, label: course.name }))
      },
    }),
    getGenericWhsCourses: build.query({
      query: (args) => ({
        urlKey: 'fetchGenericWhsCoursesUrl',
        params: args,
      }),
      transformResponse: (response) => response?.course_list || {},
    }),
    loadCrsCourseTees: build.query({
      query: (args) => ({
        urlKey: 'loadCrsCourseTeesUrl',
        params: args,
      }),
    }),
    loadScorecentreCourseTees: build.query({
      query: (args) => ({
        urlKey: 'loadScorecentreCourseTeesUrl',
        params: args,
      }),
    }),
    getWizardSelectedCourses: build.query({
      query: (args) => ({
        urlKey: 'getWizardSelectedCoursesUrl',
        params: args,
      }),
    }),
    getScorecentreWizardSelectedCourses: build.query({
      query: (args) => ({
        urlKey: 'getScorecentreWizardSelectedCoursesUrl',
        params: args,
      }),
    }),
    getWizardUnmatchedTees: build.query({
      query: (args) => ({
        urlKey: 'getWizardUnmatchedTeesUrl',
        params: args,
      }),
    }),
    getScorecentreWizardUnmatchedTees: build.query({
      query: (args) => ({
        urlKey: 'getScorecentreWizardUnmatchedTeesUrl',
        params: args,
      }),
    }),
    importCourse: build.mutation({
      query: ({ urlKey, ...params }) => ({
        urlKey,
        method: 'POST',
        params,
      }),
    }),
    getCustomers: build.query({
      query: (args) => ({
        urlKey: 'fetchCustomersUrl',
        params: args,
      }),
      transformResponse: (response) => {
        if (!response?.items?.length) {
          return [];
        }
  
        return response.items.map((item) => ({
          value: item.id,
          label: item.item,
        }));
      }
    }),
    getCustomerCourses: build.query({
      query: (args) => ({
        urlKey: 'fetchCustomerCoursesUrl',
        params: args,
      }),
      transformResponse: (response) => {
        return response.map((course) => {
          return { value: course[0], label: course[1]}
        })
      }
    }),
    copyCustomerCourse: build.mutation({
      query: (args) => ({
        urlKey: 'copyCustomerCourseUrl',
        method: 'POST',
        params: args,
      }),
    }),
    renameCourse: build.mutation({
      query: ({url, newName}) => ({
        url: url,
        method: 'PUT',
        params: { name: newName },
      })
    }),
    moveToLeague: build.mutation({
      query: ({url, checked}) => ({
        url: url,
        method: 'POST',
        params: { checked: checked },
      })
    })
  }),
})

export const {
  useGetCoursesQuery,
  useGetComplianceStatsQuery,
  useGetPossibleLocationsQuery,
  useGetCrsCoursesQuery,
  useGetScorecentreCoursesQuery,
  useGetGolfAdvisorCoursesQuery,
  useGetWhsUkCoursesQuery,
  useGetNgfCoursesForClubQuery,
  useGetGenericWhsCoursesQuery,
  useLoadCrsCourseTeesQuery,
  useLoadScorecentreCourseTeesQuery,
  useGetWizardSelectedCoursesQuery,
  useGetScorecentreWizardSelectedCoursesQuery,
  useGetWizardUnmatchedTeesQuery,
  useGetScorecentreWizardUnmatchedTeesQuery,
  useImportCourseMutation,
  useGetCustomersQuery,
  useGetCustomerCoursesQuery,
  useCopyCustomerCourseMutation,
  useRenameCourseMutation,
  useMoveToLeagueMutation,
} = coursesApi